<template>
  <div
    class="cal-toolbar"
    v-bind="passThrough('toolbar')"
  >
    <div class="cal-toolbar__group">
      <slot name="start" />

      <div
        class="cal-toolbar__nav"
        v-bind="passThrough('toolbarNav')"
      >
        <Button
          icon="chevron-left"
          severity="secondary"
          outlined
          size="sm"
          :alt="$t('ui.actions.previous')"
          @click="emit('prev')"
        />
        <Button
          icon="chevron-right"
          severity="secondary"
          outlined
          size="sm"
          :alt="$t('ui.actions.next')"
          @click="emit('next')"
        />
      </div>

      <Button
        :label="$t('ui.common.dateTime.today')"
        severity="secondary"
        outlined
        size="sm"
        :disabled="isTodayVisible"
        @click="emit('today')"
      />
    </div>

    <h2
      class="cal-toolbar__title"
      v-bind="passThrough('toolbarTitle')"
    >
      {{ title }}
    </h2>

    <div class="cal-toolbar__group cal-toolbar__group--end">
      <div
        class="cal-toolbar__views"
        role="tablist"
        v-bind="passThrough('viewSwitcher')"
      >
        <button
          v-for="option in viewOptions"
          :key="option.value"
          type="button"
          role="tab"
          class="cal-toolbar__view"
          :class="{ 'cal-toolbar__view--active': option.value === view }"
          :aria-selected="option.value === view"
          @click="emit('update:view', option.value)"
        >
          <span class="cal-toolbar__view-label">{{ option.label }}</span>
          <span class="cal-toolbar__view-short">{{ option.short }}</span>
        </button>
      </div>

      <slot name="end" />
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'

// components
import Button from '@/components/button/Button.vue'

// types
import type { CalendarViewType } from '@/types/components/calendar'

// utilities
import { useCalendarPassThrough } from '@/utilities/calendar/passthrough'

defineProps<{
  /** Period title shown in the centre */
  title: string
  /** Active view */
  view: CalendarViewType
  /** Whether today is already on screen, used to disable the Today button */
  isTodayVisible: boolean
}>()

const emit = defineEmits<{
  prev: []
  next: []
  today: []
  'update:view': [view: CalendarViewType]
}>()

const { t } = useI18n()
const passThrough = useCalendarPassThrough()

/** The three view options, with abbreviated labels for narrow screens */
const viewOptions = computed<{ value: CalendarViewType; label: string; short: string }[]>(() => [
  { value: 'month', label: t('ui.common.dateTime.month', 1), short: 'M' },
  { value: 'week', label: t('ui.common.dateTime.week', 1), short: 'W' },
  { value: 'day', label: t('ui.common.dateTime.day', 1), short: 'D' }
])
</script>

<style scoped>
.cal-toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--s-2);
  padding: var(--s-3) var(--s-4);
  border-bottom: 1px solid var(--border-secondary);
}

.cal-toolbar__group {
  display: flex;
  align-items: center;
  gap: var(--s-2);
  flex: 1 1 0;
  min-width: 0;
}

.cal-toolbar__group--end {
  justify-content: flex-end;
}

.cal-toolbar__nav {
  display: flex;
  align-items: center;
  gap: var(--s-1);
}

.cal-toolbar__title {
  flex: 0 1 auto;
  margin: 0;
  font-size: var(--text-lg);
  font-weight: 600;
  color: var(--text-primary);
  text-align: center;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.cal-toolbar__views {
  display: flex;
  align-items: center;
  padding: 0.125rem;
  background-color: var(--bg-secondary);
  border-radius: var(--rounded-md);
}

.cal-toolbar__view {
  height: 1.75rem;
  padding: 0 var(--s-3);
  font-size: var(--text-sm);
  font-weight: 500;
  /* Locale strings are stored lowercase for use mid-sentence */
  text-transform: capitalize;
  color: var(--text-secondary);
  background-color: transparent;
  border-radius: var(--rounded-sm);
  transition:
    background-color 0.12s ease,
    color 0.12s ease;
}

.cal-toolbar__view--active {
  color: var(--text-primary);
  background-color: var(--bg-primary);
  box-shadow: 0 1px 2px rgb(0 0 0 / 8%);
}

.cal-toolbar__view-short {
  display: none;
}

/* Narrow screens: stack the title above the controls and abbreviate the view switcher */
@media (max-width: 40rem) {
  .cal-toolbar {
    flex-wrap: wrap;
    justify-content: center;
    padding: var(--s-2) var(--s-3);
  }

  .cal-toolbar__title {
    order: -1;
    flex: 1 0 100%;
    font-size: var(--text-md);
  }

  .cal-toolbar__view {
    padding: 0 var(--s-2-5);
  }

  .cal-toolbar__view-label {
    display: none;
  }

  .cal-toolbar__view-short {
    display: inline;
  }
}
</style>
